import { useQuery } from 'react-query';
import { Template } from '../_types';
import { fetcher, ServerErrorResponse, performanceReportingApiUrl } from 'data_sources/constants';
import { transformApiResponseToFrontend } from './useTemplateAutoSave';

// Fetch template by ID from API
const getTemplateById = async (
  templateId: number,
  isDraft?: boolean,
  signal?: AbortSignal,
  onError?: (errors: ServerErrorResponse) => void,
  versionNumber?: number
): Promise<Template> => {
  try {
    const baseUrl = `${performanceReportingApiUrl()}/performance/templates/v1/templates/${templateId}`;
    let url = baseUrl;
    if (versionNumber !== undefined) {
      url = `${baseUrl}?versionNumber=${versionNumber}`;
    } else if (isDraft) {
      url = `${baseUrl}?isDraft=true`;
    }
    
    const response = await fetcher({
      method: 'GET',
      url,
      options: { signal },
    });

    if (response.errorDetails) {
      if (onError) onError(response);
      throw new Error(JSON.stringify(response));
    }

    // Rename sections → templateSections and fields → sectionFields to match frontend types
    // Preserve all properties from the backend (including effective* properties and numeric IDs)
    if (response.sections) {
      response.templateSections = response.sections.map((section: any, index: number) => ({
        ...section, // Keep ALL properties from backend including numeric IDs and effective* properties
        sectionFields: section.fields || [], // Rename fields to sectionFields
        collapsed: section.collapsed ?? false,
        displayOrder: section.displayOrder ?? index + 1,
      }));
    }

    // Use the comprehensive transformation function to properly map all properties
    return transformApiResponseToFrontend(response as Template);
  } catch (error: unknown) {
    if (onError && typeof error === 'object' && error !== null && 'errorDetails' in error) {
      onError(error as ServerErrorResponse);
    }
    throw error;
  }
};

export const useTemplate = (
  templateId: number,
  options = {},
  onError?: (errors: ServerErrorResponse) => void,
  isDraft?: boolean,
  versionNumber?: number
) => {
  return useQuery({
    queryKey: ['template', templateId, versionNumber !== undefined ? `version-${versionNumber}` : isDraft ? 'draft' : 'active'],
    queryFn: ({ signal }) => getTemplateById(templateId, isDraft, signal, onError, versionNumber),
    enabled: !!templateId,
    retry: (failureCount, error) => {
      // Don't retry on server errors (5xx) or client errors (4xx)
      const statusCode = (error as any)?.status || (error as any)?.response?.status;
      if (statusCode >= 400) {
        return false;
      }
      return failureCount < 3;
    },
    ...options,
  });
};

export { getTemplateById };
